import {
    CSSProperties,
    ChangeEvent,
    FC,
    InputHTMLAttributes,
    ReactNode,
    useRef,
} from 'react';

import classes from './TextInput.module.css';

interface ITextInput
    extends Omit<
        InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
        'value'
    > {
    label?: string;
    title?: string;
    name?: string;
    placeHolder?: string;
    defaultValue?: string;
    textStyle?: CSSProperties;
    containerStyle?: CSSProperties;
    isDisabled?: boolean;
    icon?: ReactNode | string;
    error?: string;
    success?: string;
    onClick?: (e?: any) => void;
    wrapClassName?: string;
    specialCase?: 'file-picker';
    value?: string | null; // re-add value as optional
}

const TextInput: FC<ITextInput> = props => {
    const {
        label,
        title,
        name,
        placeHolder,
        textStyle,
        containerStyle,
        defaultValue,
        isDisabled,
        icon,
        onClick,
        error,
        success,
        wrapClassName = '',
        specialCase,
        value,
        multiple,
        type = 'text',
        ...rest
    } = props;
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleClickWrapper = (e: React.MouseEvent) => {
        if (specialCase === 'file-picker' && inputRef.current) {
            inputRef.current.click();
            onClick && onClick(e);
        }
    };

    const handleFocus = () => {
        if (type === 'date' && inputRef.current instanceof HTMLInputElement) {
            inputRef.current.showPicker?.();
        }
    };

    const renderInput = () => {
        const sharedProps = {
            id: name,
            name,
            placeholder: placeHolder ?? label,
            disabled: isDisabled,
            className: classes.input,
            style: textStyle,
            defaultValue,
            value,
            ref: inputRef,
            type,
            multiple,
            ...rest,
        };

        if (multiple && type !== 'file') {
            return <textarea rows={4} {...(sharedProps as any)} />;
        }

        return <input {...sharedProps} />;
    };

    return (
        <div
            className={wrapClassName}
            style={{
                ...containerStyle,
                ...(specialCase === 'file-picker' && { cursor: 'pointer' }),
            }}
            onClick={handleClickWrapper}>
            {title && <span>{title}</span>}

            <div
                className={`${classes.container} ${
                    error ? classes.error : ''
                } ${success ? classes.success : ''}`}>
                {label && <span className={classes.labelHeading}>{label}</span>}

                <div
                    className={classes.inputWrapper}
                    onClick={e => {
                        e.stopPropagation();
                        handleFocus();
                        onClick(e);
                    }}>
                    {icon &&
                        (typeof icon === 'string' ? (
                            <img
                                alt={props.title || ''}
                                src={icon}
                                className={classes.leftIcon}
                                style={onClick && { cursor: 'pointer' }}
                            />
                        ) : (
                            icon
                        ))}
                    {renderInput()}
                </div>

                {rest.maxLength && (
                    <span className={classes['max-length']}>
                        {rest.maxLength}
                    </span>
                )}
            </div>

            {error && <span className={classes.errorText}>{error}</span>}
            {success && <span className={classes.successText}>{success}</span>}
        </div>
    );
};

export default TextInput;
